import React, { FC, MouseEvent, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

import * as PT from 'modules/Pricing/types'

import CollapsiblePanel from 'components/CollapsiblePanel'
import { CollapsibleDownChevron } from 'components/SVG/CollapsibleChevron'
import CircleVortex from 'components/SVG/illustration/CircleVortex'
import Spacer from 'components/Spacer'

import * as T from 'types'
import * as E from 'types/enums'

import Features from './Features'
import PackageCardPricingButton from './PackageCardPricingButton'
import PriceBox from './PriceBox'

interface IPackageType {
  type: E.PackageType
}
const CardContainer = styled.div<PT.IPricingPackageCardModeProps & IPackageType>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: ${props => `${props.mode ? 20 : 24}px 24px 24px`};
  background-color: ${props =>
    props.mode === E.PackageCardMode.Best ? props.theme.colors.blue900 : props.theme.colors.white};

  border-radius: 24px;

  z-index: 0;
  overflow: hidden;

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    height: 100%;
    padding: ${props =>
    `${props.mode && props.mode !== E.PackageCardMode.ComingSoon ? 20 : 40}px 28px 24px`};
    border-radius: 24px 24px 0 0;
  }
`

interface ITitlePropsArg extends PT.IPricingPackageCardModeProps {
  theme: any
}
const getTextColor = (props: ITitlePropsArg, defaultColor: string) => {
  if (props.mode === E.PackageCardMode.ComingSoon) return props.theme.colors.grey34

  if (props.mode === E.PackageCardMode.Best) return props.theme.colors.white

  return defaultColor
}

const Title = styled.div<PT.IPricingPackageCardModeProps>`
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  color: ${props => getTextColor(props, props.theme.colors.neutral800)};

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-weight: 900;
    font-size: 24px;
    line-height: 33px;
  }
`

const Subtitle = styled.div<PT.IPricingPackageCardModeProps>`
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  color: ${props => getTextColor(props, props.theme.colors.neutral400)};

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 14px;
    line-height: 20px;
  }
`

const StyledDivider = styled.div`
  background-color: ${props => props.theme.colors.red7};
  align-self: stretch;
  height: 1px;
`

const Tag = styled.div<PT.IPricingPackageCardModeProps>`
  border-radius: 12px;
  padding: 6px 16px;
  color: ${props => props.theme.colors.teal500};
  background-color: ${props => props.theme.colors.blue1000};
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
`

const TagContainer = styled.div`
  display: flex;
  justify-content: end;
`

const FeaturesContainer = styled.div`
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    display: none;
  }
`

const ShowFeaturesTitle = styled.div<PT.IPricingPackageCardModeProps>`
  display: flex;
  color: ${props => getTextColor(props, props.theme.colors.neutral300)};
`

interface IProps extends Partial<PT.IPricingPackageCardModeProps> {
  pricingPackage: T.IPricingPackage | undefined
  subtitle: string
  featuresIncluded: T.IPricingPackageItem[]
  purchasedPackage: E.PackageType | undefined
  packageType: E.PackageType
  selectable: boolean | undefined
  selectedPackage: E.PackageType | null
  onClickSelect(type: E.PackageType): void
  purchased?: boolean
  initialized?: boolean
  disabled?: boolean
}

const PackageCard: FC<IProps> = ({
  mode,
  subtitle,
  featuresIncluded,
  initialized,
  packageType,
  pricingPackage,
  selectable,
  selectedPackage,
  purchased,
  purchasedPackage,
  disabled,
  onClickSelect,
}) => {
  if (!pricingPackage) return null

  const theme = useContext(ThemeContext)

  const handleSelect = (event: MouseEvent) => {
    event.stopPropagation()
    onClickSelect(packageType)
  }

  const selected = selectedPackage === packageType

  const renderCustomChevronIcon = () => (
    <CollapsibleDownChevron
      width="8"
      height="6"
      fill={getTextColor({ mode, theme }, theme.colors.neutral300) || theme.colors.neutral800}
    />
  )

  const renderShowFeaturesTitle = () => (
    <ShowFeaturesTitle mode={mode}>
      <div>Show Features</div>
      <Spacer size={14} />
    </ShowFeaturesTitle>
  )

  return (
    <CardContainer
      mode={mode}
      type={packageType}
    >
      {mode && (
        <TagContainer>
          {mode === E.PackageCardMode.Best && <Tag mode={E.PackageCardMode.Best}>BEST VALUE</Tag>}
        </TagContainer>
      )}
      <Spacer
        desktopSize={2}
        hideMobile
        hideTablet
      />
      <Title mode={mode}>{pricingPackage?.name}</Title>
      <Spacer
        desktopSize={15}
        hideMobile
        hideTablet
      />
      <Subtitle mode={mode}>{subtitle}</Subtitle>
      <Spacer
        size={24}
        desktopSize={26}
      />
      <PriceBox
        mode={mode}
        pricingPackage={pricingPackage}
      />
      <Spacer
        size={24}
        desktopSize={36}
      />
      <PackageCardPricingButton
        mode={mode}
        pricingPackage={pricingPackage}
        onClickSelect={selectable ? handleSelect : undefined}
        selected={selected}
        purchased={purchased}
        purchasedPackage={purchasedPackage}
        disabled={disabled}
        initialized={initialized}
      />
      <FeaturesContainer>
        <Spacer size={24} />
        <StyledDivider />
        <Spacer size={24} />
        <CollapsiblePanel
          title={renderShowFeaturesTitle()}
          titleColor={getTextColor({ mode, theme }, theme.colors.grey33)}
          expandCustomIcon={renderCustomChevronIcon()}
          collapseCustomIcon={renderCustomChevronIcon()}
        >
          <div>
            <Features
              features={featuresIncluded}
              mode={mode}
              featureType={E.FeatureType.service}
            />
            <Spacer size={24} />
            <Features
              features={featuresIncluded}
              mode={mode}
              featureType={E.FeatureType.help}
            />
            <Spacer size={24} />
            <Features
              features={featuresIncluded}
              mode={mode}
              featureType={E.FeatureType.support}
            />
          </div>
        </CollapsiblePanel>
      </FeaturesContainer>
      {mode === E.PackageCardMode.Best && (
        <CircleVortex
          style={{ position: 'absolute', top: 177, right: 0, zIndex: -1 }}
          fill={theme.colors.transparentWhiteAlpha50}
        />
      )}
    </CardContainer>
  )
}

export default PackageCard
